import React, { useMemo, useState, useEffect } from 'react';
import { useNavigate, useLocation } from '../router';
import {
  Box,
  Drawer,
  List,
  Typography,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  IconButton,
  Tooltip,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  Dashboard as DashboardIcon,
  Campaign as CampaignIcon,
  Settings as SettingsIcon,
  PlaylistPlay as PlaylistIcon,
  Schedule as ScheduleIcon,
  Mic as MicIcon,
  Groups as GroupIcon,
  LibraryMusic as LibraryIcon,
  AccessTime as ClockIcon,
  Logout as LogoutIcon,
  GraphicEq as LiveIcon,
  SmartToy as AIIcon,
  AutoAwesome as IntelligenceIcon,
  FiberManualRecord as LoggerIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import SchedulingProgressWidget from './SchedulingProgressWidget';
import ColorModeToggle from './ColorModeToggle';
import { useColorMode } from '../contexts/ColorModeContext';
import radioSuiteLogo from '../assets/radiosuite-logo.png';

const API_URL = process.env.REACT_APP_API_URL || window.location.origin;

const drawerWidthExpanded = 256;
const drawerWidthCollapsed = 72;

// All possible menu items with their required module name for permission checking
const allMenuItems = [
  { text: 'Dashboard', icon: <DashboardIcon />, path: '/', module: null }, // always visible
  { text: 'Advertising', icon: <CampaignIcon />, path: '/advertising', module: 'advertising' },
  { text: 'Playlists', icon: <PlaylistIcon />, path: '/playlists', module: 'playlists' },
  { text: 'Clocks', icon: <ClockIcon />, path: '/schedule', module: 'clocks' },
  { text: 'Voicetrack', icon: <MicIcon />, path: '/voicetrack', module: 'voicetrack' },
  { text: 'Multi Voicetrack', icon: <GroupIcon />, path: '/voicetrack/multi', module: 'voicetrack', systemFeature: 'voicetrack_collaboration' },
  { text: 'Live', icon: <LiveIcon />, path: '/live', module: 'live', newTab: true },
  { text: 'Library', icon: <LibraryIcon />, path: '/library', module: 'library' },
  { text: 'AI Content', icon: <AIIcon />, path: '/ai', module: 'ai_content' },
  { text: 'Intelligence', icon: <IntelligenceIcon />, path: '/intelligence', module: 'intelligence' },
  { text: 'Logger', icon: <LoggerIcon />, path: '/logger', module: 'logger' },
  { text: 'Settings', icon: <SettingsIcon />, path: '/settings', module: 'settings' },
];

export default function Layout({ children }) {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const { toggleColorMode } = useColorMode();
  const { user, logout, hasModuleAccess, hasSystemFeature, isSuperAdmin, isAdmin } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const drawerWidth = sidebarOpen ? drawerWidthExpanded : drawerWidthCollapsed;

  // Filter menu items based on user's permissions
  const menuItems = useMemo(() => {
    return allMenuItems.filter(item => {
      // Dashboard is always visible to everyone
      if (item.module === null) return true;
      // A sub-feature switched off system-wide hides its entry even when the
      // parent module is available.
      if (item.systemFeature && !hasSystemFeature(item.systemFeature)) return false;
      // Studio Output now lives in Settings, so its operators also need the
      // Settings entry even when they do not have the broader settings module.
      if (item.module === 'settings') {
        return isSuperAdmin()
          || isAdmin()
          || hasModuleAccess('settings')
          || hasModuleAccess('studio_output');
      }
      // Use the permission system to check access
      return hasModuleAccess(item.module);
    });
  }, [user, hasModuleAccess, hasSystemFeature, isSuperAdmin, isAdmin]);

  const handleLogout = async () => {
    await logout();
  };

  const [branding, setBranding] = useState({ appName: '', logoUrl: '' });

  const fetchBranding = () => {
    axios.get(`${API_URL}/api/system-settings`, { withCredentials: true })
      .then((res) => {
        const data = res.data || {};
        setBranding({
          appName: data.branding_app_name || '',
          logoUrl: data.branding_logo_url || '',
        });
      })
      .catch(() => {});
  };

  useEffect(() => {
    fetchBranding();
  }, []);

  useEffect(() => {
    const onVisible = () => fetchBranding();
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, []);

  const displayName = (branding.appName || '').trim() || 'The Radio Suite';
  const logoUrl = (branding.logoUrl || '').trim();

  return (
    <Box sx={{ display: 'flex' }}>
      <Drawer
        variant="permanent"
        sx={{
          width: drawerWidth,
          flexShrink: 0,
          transition: (theme) => theme.transitions.create('width', { duration: theme.transitions.duration.enteringScreen }),
          '& .MuiDrawer-paper': {
            width: drawerWidth - 14,
            boxSizing: 'border-box',
            top: 14,
            left: 14,
            height: 'calc(100% - 28px)',
            borderRadius: '16px',
            backgroundColor: isDark ? 'rgba(11,15,23,0.62)' : 'rgba(255,255,255,0.78)',
            backgroundImage: isDark
              ? 'linear-gradient(180deg, rgba(255,255,255,0.045) 0%, rgba(255,255,255,0) 30%)'
              : 'linear-gradient(180deg, rgba(255,255,255,0.7) 0%, rgba(255,255,255,0) 30%)',
            backdropFilter: 'blur(20px) saturate(1.4)',
            WebkitBackdropFilter: 'blur(20px) saturate(1.4)',
            border: '1px solid',
            borderColor: isDark ? 'rgba(120,180,255,0.14)' : 'rgba(15,23,42,0.10)',
            boxShadow: isDark
              ? '0 24px 60px -24px rgba(0,0,0,0.85)'
              : '0 24px 60px -28px rgba(15,23,42,0.25)',
            transition: (muiTheme) => muiTheme.transitions.create('width', { duration: muiTheme.transitions.duration.enteringScreen }),
            overflowX: 'hidden',
          },
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
          <Box sx={{ px: sidebarOpen ? 2 : 0.5, py: 2, borderBottom: '1px solid', borderColor: 'divider', display: 'flex', flexDirection: sidebarOpen ? 'row' : 'column', alignItems: 'center', gap: 1, flexWrap: 'nowrap', position: 'relative' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, minWidth: 0, flex: sidebarOpen ? 1 : undefined, justifyContent: sidebarOpen ? 'flex-start' : 'center', pr: sidebarOpen ? 3.5 : 0 }}>
              <Box
                sx={{
                  width: 42,
                  height: 42,
                  borderRadius: 2.5,
                  background: isDark ? 'rgba(59,130,246,0.08)' : 'rgba(59,130,246,0.06)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  overflow: 'hidden',
                  flexShrink: 0,
                  border: isDark ? '1px solid rgba(96,165,250,0.32)' : '1px solid rgba(59,130,246,0.30)',
                  boxShadow: isDark
                    ? '0 0 0 3px rgba(59,130,246,0.08), 0 0 22px -4px rgba(34,211,238,0.55)'
                    : '0 0 0 3px rgba(59,130,246,0.08)',
                }}
              >
                <img
                  src={logoUrl ? (logoUrl.startsWith('http') ? logoUrl : `${API_URL}${logoUrl}`) : radioSuiteLogo}
                  alt="RadioSuite logo"
                  style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                  onError={(e) => {
                    if (e.target.src !== radioSuiteLogo) {
                      e.target.src = radioSuiteLogo;
                    }
                  }}
                />
              </Box>
              {sidebarOpen && (
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Typography
                    variant="subtitle1"
                    title={displayName}
                    sx={{
                      fontFamily: '"Space Grotesk", "Inter", system-ui, sans-serif',
                      fontWeight: 700,
                      lineHeight: 1.2,
                      letterSpacing: '-0.02em',
                      fontSize: '0.92rem',
                      overflow: 'hidden',
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                    }}
                  >
                    {displayName}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{
                      color: isDark ? 'rgba(103,232,249,0.55)' : 'rgba(8,145,178,0.75)',
                      letterSpacing: '0.16em',
                      fontWeight: 700,
                      fontSize: '0.58rem',
                    }}
                    noWrap
                  >
                    PLAYOUT
                  </Typography>
                </Box>
              )}
            </Box>
            <IconButton
              size="small"
              onClick={() => setSidebarOpen((o) => !o)}
              sx={{
                flexShrink: 0,
                ...(sidebarOpen && {
                  position: 'absolute',
                  right: 6,
                  top: 8,
                }),
              }}
              aria-label={sidebarOpen ? 'Collapse sidebar' : 'Expand sidebar'}
            >
              {sidebarOpen ? <ChevronLeftIcon /> : <ChevronRightIcon />}
            </IconButton>
          </Box>
          <Box sx={{ overflow: 'auto', flex: 1, py: 1.5 }}>
            {sidebarOpen && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 3, pb: 0.5 }}>
                <Box sx={{ width: 4, height: 4, borderRadius: '1px', bgcolor: isDark ? '#22d3ee' : '#0891b2' }} />
                <Typography
                  variant="overline"
                  sx={{ color: 'text.disabled', fontSize: '0.62rem', lineHeight: 1.6 }}
                >
                  Menu
                </Typography>
              </Box>
            )}
            <List disablePadding>
              {menuItems.map((item) => {
                const selected = location.pathname === item.path;
                const button = (
                  <ListItem key={item.text} disablePadding sx={{ px: 1.25, mb: 0.5 }}>
                    <ListItemButton
                      selected={selected}
                      onClick={() => {
                        if (item.newTab) {
                          window.open(item.path, '_blank', 'noopener,noreferrer');
                        } else {
                          navigate(item.path);
                        }
                      }}
                      aria-label={item.newTab ? `${item.text} (opens in a new tab)` : item.text}
                      sx={{
                        position: 'relative',
                        borderRadius: 2,
                        py: 1.1,
                        border: '1px solid transparent',
                        justifyContent: sidebarOpen ? 'flex-start' : 'center',
                        px: sidebarOpen ? 2 : 1.5,
                        transition: 'background-color 140ms ease, color 140ms ease, border-color 140ms ease',
                        '&.Mui-selected': {
                          color: isDark ? '#eaf6ff' : '#0f172a',
                          background: isDark
                            ? 'linear-gradient(90deg, rgba(59,130,246,0.30) 0%, rgba(34,211,238,0.14) 100%)'
                            : 'linear-gradient(90deg, rgba(59,130,246,0.16) 0%, rgba(8,145,178,0.10) 100%)',
                          border: isDark ? '1px solid rgba(34,211,238,0.42)' : '1px solid rgba(59,130,246,0.30)',
                          boxShadow: isDark
                            ? '0 0 24px -4px rgba(34,211,238,0.45), inset 0 0 16px rgba(34,211,238,0.08)'
                            : '0 6px 18px -10px rgba(59,130,246,0.4)',
                          '&::before': {
                            content: '""',
                            position: 'absolute',
                            left: 4,
                            top: '22%',
                            bottom: '22%',
                            width: 3,
                            borderRadius: 3,
                            background: 'linear-gradient(180deg, #60a5fa, #22d3ee)',
                            boxShadow: isDark ? '0 0 10px rgba(34,211,238,0.9)' : 'none',
                          },
                          '&:hover': {
                            background: isDark
                              ? 'linear-gradient(90deg, rgba(59,130,246,0.36) 0%, rgba(34,211,238,0.18) 100%)'
                              : 'linear-gradient(90deg, rgba(59,130,246,0.20) 0%, rgba(8,145,178,0.13) 100%)',
                          },
                          '& .MuiListItemIcon-root': {
                            color: isDark ? '#67e8f9' : '#0369a1',
                            filter: isDark ? 'drop-shadow(0 0 6px rgba(34,211,238,0.65))' : 'none',
                          },
                        },
                        '&:hover': {
                          bgcolor: isDark ? 'rgba(148,163,184,0.08)' : 'rgba(15,23,42,0.05)',
                        },
                      }}
                    >
                      <ListItemIcon sx={{ color: selected ? (isDark ? '#7dd3fc' : '#0369a1') : 'text.secondary', minWidth: sidebarOpen ? 38 : 0, justifyContent: 'center', transition: 'color 140ms ease', '& .MuiSvgIcon-root': { fontSize: 21 } }}>
                        {item.icon}
                      </ListItemIcon>
                      {sidebarOpen && (
                        <ListItemText primary={item.text} primaryTypographyProps={{ fontWeight: selected ? 600 : 500, fontSize: '0.875rem' }} />
                      )}
                    </ListItemButton>
                  </ListItem>
                );
                return sidebarOpen ? button : (
                  <Tooltip key={item.text} title={item.text} placement="right">
                    {button}
                  </Tooltip>
                );
              })}
            </List>
          </Box>
          {user && (
            <Box sx={{ p: sidebarOpen ? 2 : 1, borderTop: '1px solid', borderColor: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(16,24,40,0.08)', bgcolor: isDark ? 'rgba(255,255,255,0.02)' : 'rgba(16,24,40,0.025)' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: sidebarOpen ? 'flex-start' : 'center', gap: 1.5, mb: 1 }}>
                <Box
                  sx={{
                    width: 36,
                    height: 36,
                    borderRadius: '50%',
                    background: isDark
                      ? 'linear-gradient(145deg, rgba(59,130,246,0.26), rgba(34,211,238,0.10))'
                      : 'linear-gradient(145deg, rgba(59,130,246,0.18), rgba(8,145,178,0.08))',
                    border: isDark ? '1px solid rgba(96,165,250,0.40)' : '1px solid rgba(59,130,246,0.32)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                  }}
                >
                  <Typography
                    sx={{
                      fontFamily: '"Space Grotesk", "Inter", system-ui, sans-serif',
                      fontWeight: 700,
                      fontSize: '0.85rem',
                      lineHeight: 1,
                      color: isDark ? '#7dd3fc' : '#0369a1',
                    }}
                  >
                    {(user.username || '?').charAt(0).toUpperCase()}
                  </Typography>
                </Box>
                {sidebarOpen && (
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="body2" noWrap sx={{ fontWeight: 600 }}>
                      {user.username}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'capitalize' }}>
                      {user.role}
                    </Typography>
                  </Box>
                )}
              </Box>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: sidebarOpen ? 'flex-start' : 'center',
                  px: sidebarOpen ? 1 : 0,
                  mb: 0.5,
                  gap: 0.5,
                }}
              >
                <ColorModeToggle />
                {sidebarOpen && (
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    onClick={toggleColorMode}
                    sx={{ cursor: 'pointer', userSelect: 'none' }}
                  >
                    {isDark ? 'Dark mode' : 'Light mode'}
                  </Typography>
                )}
              </Box>
              <Tooltip title="Logout" placement="right">
                <ListItemButton
                  onClick={handleLogout}
                  sx={{
                    borderRadius: 2,
                    color: 'text.secondary',
                    justifyContent: sidebarOpen ? 'flex-start' : 'center',
                    px: sidebarOpen ? 2 : 1.5,
                    '&:hover': { color: 'secondary.main', bgcolor: 'action.selected' },
                  }}
                >
                  <ListItemIcon sx={{ minWidth: sidebarOpen ? 36 : 0, justifyContent: 'center' }}>
                    <LogoutIcon fontSize="small" />
                  </ListItemIcon>
                  {sidebarOpen && <ListItemText primary="Logout" primaryTypographyProps={{ variant: 'body2' }} />}
                </ListItemButton>
              </Tooltip>
            </Box>
          )}
        </Box>
      </Drawer>
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
          bgcolor: 'transparent',
          minHeight: 'var(--rs-vh)',
          position: 'relative',
          zIndex: 1,
          animation: 'page-enter 380ms ease-out',
          '@media (prefers-reduced-motion: reduce)': {
            animation: 'none',
          },
        }}
      >
        {children}
      </Box>
      <SchedulingProgressWidget />
    </Box>
  );
}
